import React, { useEffect, useRef, useState, useMemo } from 'react';

interface FrameSimulatorProps {
    widthMm: number;
    heightMm: number;
    imageUrl?: string;
    frameType?: string; // 'aluminum_black', 'wood_natural', etc. or undefined/null/'none'
    mode: 'product' | 'installation';
}

export const FrameSimulator: React.FC<FrameSimulatorProps> = ({
    widthMm,
    heightMm,
    imageUrl,
    frameType,
    mode
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [images, setImages] = useState<Record<string, HTMLImageElement>>({});
    const [loaded, setLoaded] = useState(false);
    const [artworkImage, setArtworkImage] = useState<HTMLImageElement | null>(null);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

    // Constants
    const FRAME_WIDTH_PX = 54; // Visual width of the frame parts in the asset
    const DPI = 72;
    const PIXELS_PER_MM = DPI / 25.4;

    // Check if frame is selected
    const hasFrame = !!frameType && frameType !== 'none';

    // Frame overlap (only if frame exists)
    const FRAME_OVERLAP_MM = hasFrame ? 9 : 0; // 9mm per side overlap
    const TOTAL_OVERLAP_MM = FRAME_OVERLAP_MM * 2;

    // Load artwork image
    useEffect(() => {
        if (!imageUrl) {
            setArtworkImage(null);
            return;
        }
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => setArtworkImage(img);
        img.src = imageUrl;
    }, [imageUrl]);

    // Load simulator assets
    useEffect(() => {
        const loadImages = async () => {
            const imagePaths = {
                cornerTopLeft: '/assets/simulator/aluminum_black_corner_top_left.png',
                cornerTopRight: '/assets/simulator/aluminum_black_corner_top_right.png',
                edgeTop: '/assets/simulator/aluminum_black_edge_top.png',
                edgeBottom: '/assets/simulator/aluminum_black_edge_bottom.png',
                edgeLeft: '/assets/simulator/aluminum_black_edge_left.png',
                edgeRight: '/assets/simulator/aluminum_black_edge_right.png',
                background: '/assets/simulator/room_background.jpg',
                people: '/assets/simulator/people.png'
            };

            const loadedImages: Record<string, HTMLImageElement> = {};
            const promises = Object.entries(imagePaths).map(([key, src]) => {
                return new Promise<void>((resolve) => {
                    const img = new Image();
                    img.onload = () => {
                        loadedImages[key] = img;
                        resolve();
                    };
                    img.onerror = () => {
                        console.warn(`Failed to load image: ${src}`);
                        resolve();
                    };
                    img.src = src;
                });
            });

            await Promise.all(promises);
            setImages(loadedImages);
            setLoaded(true);
        };

        loadImages();
    }, []);

    // Monitor container size
    useEffect(() => {
        if (!containerRef.current) return;
        const updateSize = () => {
            if (containerRef.current) {
                setContainerSize({
                    width: containerRef.current.clientWidth,
                    height: containerRef.current.clientHeight
                });
            }
        };
        updateSize();
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    // Layout Calculation
    const layout = useMemo(() => {
        if (containerSize.width === 0 || containerSize.height === 0) return null;

        const B1_WIDTH_MM = 728;
        const B1_HEIGHT_MM = 1030;
        const B1_REDUCTION = 0.7;

        // Paper Dimensions
        const paperWidthMm = widthMm;
        const paperHeightMm = heightMm;

        // Visual Dimensions (what is drawn)
        // If frame exists, the visual size includes the frame width but subtracts overlap from the inner opening.
        // Actually, let's define the "Outer Box" size.
        // Inner Opening = Paper Size - Overlap
        // Outer Frame = Inner Opening + (Frame Width * 2)

        // Frame Width in MM (approximate for calculation)
        const frameWidthMm = hasFrame ? (FRAME_WIDTH_PX / PIXELS_PER_MM) : 0;

        const innerWidthMm = paperWidthMm - TOTAL_OVERLAP_MM;
        const innerHeightMm = paperHeightMm - TOTAL_OVERLAP_MM;

        const outerWidthMm = innerWidthMm + (frameWidthMm * 2);
        const outerHeightMm = innerHeightMm + (frameWidthMm * 2);

        // A3 Area Check (297 * 420)
        const A3_AREA = 297 * 420;
        const currentArea = outerWidthMm * outerHeightMm;
        const isSmallerThanA3 = currentArea < A3_AREA;

        let scale = 1;
        let canvasTop = 0;
        let canvasLeft = 0;
        let backgroundZoom = 1.0;

        if (mode === 'installation') {
            // Installation Mode Logic
            backgroundZoom = isSmallerThanA3 ? 1.2 : 1.0;

            // B1 Reference Calculation
            const B1_WIDTH_PX = B1_WIDTH_MM * PIXELS_PER_MM;
            const B1_HEIGHT_PX = B1_HEIGHT_MM * PIXELS_PER_MM;

            // Scale B1 to fit container (reference scale)
            const B1_fitScaleX = containerSize.width / B1_WIDTH_PX;
            const B1_fitScaleY = containerSize.height / B1_HEIGHT_PX;
            const B1_baseScale = Math.min(B1_fitScaleX, B1_fitScaleY, 1);
            const B1_finalScale = B1_baseScale * B1_REDUCTION;

            // Apply background zoom and A3 adjustment
            scale = B1_finalScale * backgroundZoom;
            if (isSmallerThanA3) {
                scale = scale * 0.7;
            }

            // Positioning
            const B1_SCALED_HEIGHT = B1_HEIGHT_PX * B1_finalScale; // Height of B1 in the view
            const B1_TOP_PERCENT = isSmallerThanA3 ? -15 : 2;

            // Center Y of B1 relative to container
            const B1_CENTER_Y = (containerSize.height * (50 + B1_TOP_PERCENT) / 100);
            // Wait, requirement says "Top from -15%". Usually means offset from center or top?
            // "上から-15%の位置に中央を配置" -> Center is at -15% from top? That would be off screen.
            // Likely means: Center is shifted up by 15% of height? Or Top edge is at -15%?
            // Let's assume it means "Center Y is at (50 - 15)% of container height".
            // If "上から2%", Center Y is at (50 + 2)%? Or just 2% from top?
            // "上から2%の位置に中央を配置" -> Center Y = 2% of container height.
            // Let's try interpreting as: Center Y position %

            const centerYPercent = isSmallerThanA3 ? 35 : 40; // Adjusting based on "上から..." interpretation.
            // Let's stick to a safe interpretation:
            // A3<: Higher up (to show more context/person?).
            // A3>=: Lower down?
            // Let's use the values as offsets from 50%.
            // If "上から-15%", maybe it means 35% (50-15).
            // If "上から2%", maybe it means 52% (50+2).

            const targetCenterY = containerSize.height * (isSmallerThanA3 ? 0.35 : 0.45);
            const targetCenterX = containerSize.width * 0.5;

            const currentOuterWidthPx = outerWidthMm * PIXELS_PER_MM * scale;
            const currentOuterHeightPx = outerHeightMm * PIXELS_PER_MM * scale;

            canvasTop = targetCenterY - (currentOuterHeightPx / 2);
            canvasLeft = targetCenterX - (currentOuterWidthPx / 2);

        } else {
            // Product Mode Logic
            const padding = 40;
            const availWidth = containerSize.width - padding * 2;
            const availHeight = containerSize.height - padding * 2;

            const outerWidthPxRaw = outerWidthMm * PIXELS_PER_MM;
            const outerHeightPxRaw = outerHeightMm * PIXELS_PER_MM;

            const scaleX = availWidth / outerWidthPxRaw;
            const scaleY = availHeight / outerHeightPxRaw;
            scale = Math.min(scaleX, scaleY); // Fill available space

            // Center in container
            canvasTop = (containerSize.height - (outerHeightPxRaw * scale)) / 2;
            canvasLeft = (containerSize.width - (outerWidthPxRaw * scale)) / 2;
        }

        return {
            scale,
            canvasTop,
            canvasLeft,
            outerWidthMm,
            outerHeightMm,
            innerWidthMm,
            innerHeightMm,
            isSmallerThanA3,
            backgroundZoom,
            frameWidthMm
        };
    }, [widthMm, heightMm, containerSize, mode, hasFrame, FRAME_OVERLAP_MM, TOTAL_OVERLAP_MM]);

    // Canvas Rendering
    useEffect(() => {
        if (!loaded || !canvasRef.current || !layout) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const { scale, outerWidthMm, outerHeightMm, innerWidthMm, innerHeightMm } = layout;

        // Canvas Size
        const dpr = window.devicePixelRatio || 1;
        const finalWidth = outerWidthMm * PIXELS_PER_MM * scale;
        const finalHeight = outerHeightMm * PIXELS_PER_MM * scale;

        canvas.width = finalWidth * dpr;
        canvas.height = finalHeight * dpr;
        canvas.style.width = `${finalWidth}px`;
        canvas.style.height = `${finalHeight}px`;
        canvas.style.position = 'absolute';
        canvas.style.top = `${layout.canvasTop}px`;
        canvas.style.left = `${layout.canvasLeft}px`;

        ctx.scale(dpr, dpr);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.clearRect(0, 0, finalWidth, finalHeight);

        // Draw Artwork
        if (artworkImage) {
            // Paper Size (Full size, drawn underneath frame)
            // Paper is centered in the "Inner Box" but extended by overlap
            // Inner Box starts at (FrameWidth) offset
            // Paper starts at (FrameWidth - Overlap) offset

            const frameWidthPxScaled = hasFrame ? (FRAME_WIDTH_PX * scale) : 0;
            const overlapPxScaled = hasFrame ? (FRAME_OVERLAP_MM * PIXELS_PER_MM * scale) : 0;

            const paperX = frameWidthPxScaled - overlapPxScaled;
            const paperY = frameWidthPxScaled - overlapPxScaled;
            const paperW = widthMm * PIXELS_PER_MM * scale;
            const paperH = heightMm * PIXELS_PER_MM * scale;

            // Draw white background for paper
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(paperX, paperY, paperW, paperH);

            // Draw Image (fit to paper)
            const imgAspect = artworkImage.width / artworkImage.height;
            const paperAspect = paperW / paperH;

            let drawW, drawH, drawX, drawY;

            if (imgAspect > paperAspect) {
                drawW = paperW;
                drawH = paperW / imgAspect;
                drawX = paperX;
                drawY = paperY + (paperH - drawH) / 2;
            } else {
                drawH = paperH;
                drawW = paperH * imgAspect;
                drawY = paperY;
                drawX = paperX + (paperW - drawW) / 2;
            }

            // Clip to paper area
            ctx.save();
            ctx.beginPath();
            ctx.rect(paperX, paperY, paperW, paperH);
            ctx.clip();
            ctx.drawImage(artworkImage, drawX, drawY, drawW, drawH);
            ctx.restore();
        }

        // Draw Frame (if exists)
        if (hasFrame) {
            const scaledCornerSize = Math.max(images.cornerTopLeft?.width || 0, images.cornerTopLeft?.height || 0) * scale;
            const scaledFrameWidth = FRAME_WIDTH_PX * scale;

            // Helper to draw edge
            const drawEdge = (side: 'top' | 'bottom' | 'left' | 'right', x: number, y: number, w: number, h: number) => {
                let img;
                switch (side) {
                    case 'top': img = images.edgeTop; break;
                    case 'bottom': img = images.edgeBottom; break;
                    case 'left': img = images.edgeLeft; break;
                    case 'right': img = images.edgeRight; break;
                }
                if (!img) return;

                if (side === 'top' || side === 'bottom') {
                    ctx.drawImage(img, 0, 0, img.width, img.height, x, y, w, scaledFrameWidth);
                } else {
                    ctx.drawImage(img, 0, 0, img.width, img.height, x, y, scaledFrameWidth, h);
                }
            };

            // Helper to draw corner
            const drawCorner = (pos: 'tl' | 'tr' | 'bl' | 'br', x: number, y: number) => {
                let img;
                let rotation = 0;
                switch (pos) {
                    case 'tl': img = images.cornerTopLeft; break;
                    case 'tr': img = images.cornerTopRight; break;
                    case 'bl': img = images.cornerTopRight; rotation = 180; break;
                    case 'br': img = images.cornerTopLeft; rotation = 180; break;
                }
                if (!img) return;

                const size = scaledCornerSize;
                ctx.save();
                const cx = x + size / 2;
                const cy = y + size / 2;
                ctx.translate(cx, cy);
                ctx.rotate(rotation * Math.PI / 180);
                ctx.translate(-cx, -cy);
                ctx.drawImage(img, x, y, size, size);
                ctx.restore();
            };

            // Edges
            const topEdgeWidth = finalWidth - scaledCornerSize * 2;
            if (topEdgeWidth > 0) drawEdge('top', scaledCornerSize, 0, topEdgeWidth, scaledFrameWidth);

            const bottomEdgeWidth = finalWidth - scaledCornerSize * 2;
            if (bottomEdgeWidth > 0) drawEdge('bottom', scaledCornerSize, finalHeight - scaledFrameWidth, bottomEdgeWidth, scaledFrameWidth);

            const leftEdgeHeight = finalHeight - scaledCornerSize * 2;
            if (leftEdgeHeight > 0) drawEdge('left', 0, scaledCornerSize, scaledFrameWidth, leftEdgeHeight);

            const rightEdgeHeight = finalHeight - scaledCornerSize * 2;
            if (rightEdgeHeight > 0) drawEdge('right', finalWidth - scaledFrameWidth, scaledCornerSize, scaledFrameWidth, rightEdgeHeight);

            // Corners
            drawCorner('tl', 0, 0);
            drawCorner('tr', finalWidth - scaledCornerSize, 0);
            drawCorner('br', finalWidth - scaledCornerSize, finalHeight - scaledCornerSize);
            drawCorner('bl', 0, finalHeight - scaledCornerSize);
        }

    }, [loaded, layout, artworkImage, hasFrame, images, widthMm, heightMm, FRAME_OVERLAP_MM, PIXELS_PER_MM, FRAME_WIDTH_PX]);

    // Render Overlays (Person, Dimensions)
    const renderOverlays = () => {
        if (!layout || mode !== 'installation') return null;

        const { isSmallerThanA3, canvasTop, canvasLeft, outerWidthMm, outerHeightMm, scale } = layout;

        // Person Style
        // Left: 70%
        // Bottom: A3< 30px, A3>= 50px
        const personStyle: React.CSSProperties = {
            position: 'absolute',
            left: '70%',
            bottom: isSmallerThanA3 ? '30px' : '50px',
            // height: '300px', // Removed duplicate
            // The person is 170cm.
            // In the room, B1 (103cm) is B1_finalScale * PIXELS_PER_MM pixels high.
            // So Person Height = (1700mm / 1030mm) * B1_Height_Px
            // Or simply: 1700 * PIXELS_PER_MM * scale / backgroundZoom (since scale includes zoom)
            // Let's use the scale factor derived from B1.
            // scale = B1_finalScale * backgroundZoom.
            // Person should scale with the room (B1_finalScale).
            // So Person Height = 1700 * PIXELS_PER_MM * (scale / backgroundZoom).
            height: `${1700 * PIXELS_PER_MM * (scale / layout.backgroundZoom)}px`,
            objectFit: 'contain',
            pointerEvents: 'none',
            opacity: 0.9,
            zIndex: 10
        };

        // Dimensions
        // Left (Height): Vertical line + Text
        // Bottom (Width): Horizontal line + Text

        const frameWidthPx = outerWidthMm * PIXELS_PER_MM * scale;
        const frameHeightPx = outerHeightMm * PIXELS_PER_MM * scale;

        const distFromFrame = 20; // px
        const textOffset = 50; // px

        return (
            <>
                {/* Person */}
                {images.people && (
                    <img
                        src={images.people.src}
                        alt="Person (170cm)"
                        style={personStyle}
                    />
                )}

                {/* Dimensions - Left (Height) */}
                <div style={{
                    position: 'absolute',
                    top: canvasTop,
                    left: canvasLeft - distFromFrame,
                    height: frameHeightPx,
                    width: '1px',
                    backgroundColor: 'black'
                }}>
                    {/* Top tick */}
                    <div style={{ position: 'absolute', top: 0, left: '-5px', width: '10px', height: '1px', backgroundColor: 'black' }} />
                    {/* Bottom tick */}
                    <div style={{ position: 'absolute', bottom: 0, left: '-5px', width: '10px', height: '1px', backgroundColor: 'black' }} />
                    {/* Text */}
                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        right: `${textOffset}px`,
                        transform: 'translateY(-50%)',
                        whiteSpace: 'nowrap',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: '#333'
                    }}>
                        {Math.round(outerHeightMm)} mm
                    </div>
                </div>

                {/* Dimensions - Bottom (Width) */}
                <div style={{
                    position: 'absolute',
                    top: canvasTop + frameHeightPx + distFromFrame,
                    left: canvasLeft,
                    width: frameWidthPx,
                    height: '1px',
                    backgroundColor: 'black'
                }}>
                    {/* Left tick */}
                    <div style={{ position: 'absolute', left: 0, top: '-5px', width: '1px', height: '10px', backgroundColor: 'black' }} />
                    {/* Right tick */}
                    <div style={{ position: 'absolute', right: 0, top: '-5px', width: '1px', height: '10px', backgroundColor: 'black' }} />
                    {/* Text */}
                    <div style={{
                        position: 'absolute',
                        left: '50%',
                        top: `${textOffset}px`,
                        transform: 'translateX(-50%)',
                        whiteSpace: 'nowrap',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: '#333'
                    }}>
                        {Math.round(outerWidthMm)} mm
                    </div>
                </div>
            </>
        );
    };

    return (
        <div
            ref={containerRef}
            className="relative w-full h-full overflow-hidden flex items-center justify-center bg-gray-50"
            style={{
                backgroundImage: mode === 'installation' && images.background ? `url('${images.background.src}')` : 'none',
                backgroundSize: layout?.isSmallerThanA3 ? 'cover' : 'contain',
                backgroundPosition: layout?.isSmallerThanA3 ? 'center bottom' : 'center',
                backgroundRepeat: 'no-repeat'
            }}
        >
            <canvas ref={canvasRef} />
            {renderOverlays()}
        </div>
    );
};
